


export type Stat =
  | "strength"      
  | "dexterity"     
  | "intelligence"  
  | "wisdom"        
  | "constitution"; 

export type Stats = Record<Stat, number>;

export type ClassId = "warrior" | "priest" | "wizard";

export type DomainId =
  | "steel" | "blood"
  | "light" | "darkness" | "nature"
  | "fire" | "ice" | "shadow";

export interface ClassDef {
  id: ClassId;
  name: string;
  keyStat: Stat;
  description: string;
  domainIds: DomainId[];
  
  startingStats: Stats;
  
  startingClassCards: string[];
}

export interface DomainDef {
  id: DomainId;
  name: string;
  classId: ClassId;
  keyStat: Stat;
  description: string;
}




export interface DiceNotation {
  count: number;
  sides: number;
}

export type StatusEffectId = "poison" | "bleed" | "hitBoost" | "damageDie" | "statDebuff" | "unbreakable" | "stun" | "weak" | "levelDown" | "regen" | "blessing" | "chill" | "numbness";

export interface StatusEffect {
  id: StatusEffectId;
  stacks: number;
  ttl: number;
  dieSides?: number;
  stat?: Stat;
  delta?: number;
}

export type CardEffect =
  | { type: "damage"; base: number; dice?: DiceNotation; statScale?: { stat: Stat; mult: number }; levelScale?: { mult: number }; hpBelow?: { ofSelf: boolean; pct: number; replaceDice?: DiceNotation }; bonusIfTargetHasDebuff?: { dice: DiceNotation } }
  | { type: "heal"; base: number; dice?: DiceNotation; statScale?: { stat: Stat; mult: number }; levelScale?: { mult: number } }
  | { type: "draw"; count: number }
  | { type: "bonusAttack"; scope: "nextAttack" | "allThisTurn"; bonusFromStat: Stat }
  | { type: "armorDebuff"; value: number; onlyOnCrit?: boolean }
  | { type: "bonusDraw"; count: number; onlyOnCrit?: boolean }
  | { type: "armorBuff"; base: number; dice?: DiceNotation; statScale?: { stat: Stat; mult: number } }
  | { type: "selfDamage"; base: number; dice?: DiceNotation; statScale?: { stat: Stat; mult: number } }
  | { type: "applyStatus"; status: StatusEffectId; target: "self" | "enemy"; stacks: number; ttl?: number; dieSides?: number; stat?: Stat; delta?: number; onlyOnHit?: boolean };

export interface StatusDef {
  label: string;
  defaultTarget: "self" | "enemy";
  params: Array<"dieSides" | "stat" | "delta">;
}

export const STATUS_DEFS: Record<StatusEffectId, StatusDef> = {
  poison: { label: "Яд", defaultTarget: "enemy", params: ["dieSides"] },
  bleed: { label: "Кровотечение", defaultTarget: "enemy", params: ["dieSides"] },
  statDebuff: { label: "Дебафф стата", defaultTarget: "enemy", params: ["stat", "delta"] },
  stun: { label: "Оглушение", defaultTarget: "enemy", params: [] },
  hitBoost: { label: "Меткость", defaultTarget: "self", params: [] },
  damageDie: { label: "Доп. кубик урона", defaultTarget: "self", params: ["dieSides"] },
  unbreakable: { label: "Несокрушимость", defaultTarget: "self", params: [] },
  weak:      { label: "Слабость",      defaultTarget: "enemy", params: [] },
  levelDown: { label: "Ослабление",    defaultTarget: "enemy", params: [] },
  regen:     { label: "Исцеление",     defaultTarget: "self",  params: [] },
  blessing:  { label: "Благословение", defaultTarget: "self",  params: [] },
  chill:     { label: "Заморозка",     defaultTarget: "enemy", params: [] },
  numbness:  { label: "Оцепенение",    defaultTarget: "enemy", params: [] },
};


export interface CardVariant {
  
  cost: number;
  effects: CardEffect[];
  
  description: string;
}


export interface CardDef {
  id: string;
  name: string;

  classId?: ClassId;
  enhancements: Partial<Record<DomainId, { hope?: CardEffect[]; fear?: CardEffect[] }>>;

  kind: "attack" | "spell" | "action";
  rarity: "common" | "uncommon" | "rare" | "epic";
  hope: CardVariant;
  fear: CardVariant;
}


export type EquipmentSlot = "head" | "body" | "hands" | "weapon" | "accessory";

export type ArmorClass = "light" | "medium" | "heavy";

export interface EquipmentDef {
  id: string;
  name: string;
  slot: EquipmentSlot;

  statBonuses: Partial<Stats>;

  armor: number;

  evasionMod: number;

  armorClass?: ArmorClass;

  critBonusPct?: number;

  grantsCards: string[];
  description: string;
}



export interface D20Roll {
  raw: number;      
  modifier: number;
  total: number;    
}


export interface D12PairRoll {
  hope: number;  
  fear: number;  
}


export interface HitResult {
  source: "hero" | "enemy";
  roll: D20Roll;
  targetEvasion: number;
  hit: boolean;
  crit: boolean;
  enemyRolls?: { a: number; b: number };
}


export interface ManaPool {
  hope: number;     
  maxHope: number;  
  fear: number;     
  maxFear: number;  
}

export interface BattleActorState {
  name: string;
  hp: number;
  maxHp: number;

  armor: number;

  evasion: number;

  mana: ManaPool;

  stats: Stats;

  keyStat: Stat;

  hitMod: number;

  critThreshold?: number;

  level: number;

  statusEffects?: StatusEffect[];
}

export interface BattleState {
  turn: number;

  activeSide: "hero" | "enemy";

  heroDomainId: DomainId;

  hero: BattleActorState;
  enemy: BattleActorState;

  
  heroHand: CardDef[];
  heroDrawPile: CardDef[];
  heroDiscardPile: CardDef[];

  
  enemyDeck: CardDef[];
  enemyDeckIdx: number;

  
  heroBonusNextAttack: number;
  heroBonusAllAttacks: number;

  
  heroFearAccumulated: number;

  
  log: BattleLogEntry[];
}

export interface BattleLogEntry {
  turn: number;
  side: "hero" | "enemy";
  text: string;
}


export interface LootRow {
  
  rollMin: number;
  rollMax: number;
  
  kind: "nothing" | "card" | "equipment" | "gold";
  payload?: {
    
    cardIds?: string[];
    
    equipmentIds?: string[];
    
    goldMin?: number;
    goldMax?: number;
  };
  label: string;
}

export type LootTable = LootRow[];

export type ChoiceKind =
  | { kind: "addCard"; cardId: string }
  | { kind: "statPlusOne"; stat: Stat };

export interface ChoiceOption {
  id: string;
  label: string;
  choice: ChoiceKind;
}

export interface ChoiceStep {
  id: string;
  title: string;
  options: ChoiceOption[];
}

export interface CardPickSpec {
  cards?: string[];
}


export interface EnemyTemplate {
  id: string;
  name: string;
  level: number;
  maxHp: number;
  hpPerLevel: number;
  armor: number;
  evasion: number;

  hitMod: number;

  critThreshold?: number;

  deck: string[];

  lootTableId: string;

  xpReward: number;

  reputationReward: number;
}


export type DifficultyLevel = "easy" | "normal" | "hard";

export interface DifficultyModifiers {
  enemyHpMult: number;
  enemyArmorBonus: number;
  enemyHitBonus: number;
  townCostMult: number;
  lootQualityMod: number;
}


export type BuildingId = "forge" | "tavern" | "guild" | "temple";

export type ReputationUpgradeId =
  | "hunter_luck"      
  | "sharp_eye"        
  | "domain_knowledge" 
  | "trade_ties";      

export interface ReputationUpgradeDef {
  id: ReputationUpgradeId;
  name: string;
  description: string;
  
  costs: number[];
  
  effectPerLevel: string;
}


export interface BattleHeroSnapshot {
  name: string;
  classId: ClassId;
  domainId: DomainId;
  stats: Stats;
  hp: number;
  maxHp: number;
  armor: number;
  evasion: number;

  keyStat: Stat;

  hitMod: number;
  critThreshold: number;

  level: number;

  deck: string[];

  fearAccumulated: number;

  reputationUpgrades: Partial<Record<ReputationUpgradeId, number>>;
  difficulty: DifficultyLevel;
}
